// PDF del Plan de Emergencias v2 (PE-B1), con jsPDF como los demás informes (permiso-pdf.ts). PE-C1: el cuerpo sigue el
// orden de los 8 capítulos (armarVistaPorCapitulos reagrupa las secciones de PASO0_DISENO.md §4.3 que arma
// armarVistaPrevia, sin cambiar su contenido) y la continuidad del negocio va al final solo si tiene datos. Las
// versiones YA aprobadas no cambian: su PDF quedó guardado al aprobar y se descarga tal cual (nunca se regenera).
// Estructura de antes: portada, secciones 1 a 17,
// resultados con su traza (norma, artículo o tabla, umbral, valor y estado), plan de acción (las acciones con
// "origen resuelto" van aparte), firmas (PE-26: quien elabora y representante legal; espacio para el sello de los
// bomberos) y anexo de pendientes (todo lo "por validar", con qué falta y en qué paso se completa).
// Textos base: solo DE 255, AM 1257 y RTQ con su cita; lo que no tiene fuente dice "pendiente de texto base" (PE-33).
// El borrador lleva la marca de agua "BORRADOR" en todas las páginas; la versión aprobada no.

import { jsPDF } from 'jspdf';
import { ETIQUETA_ESTADO_ACCION, origenResuelto, type FilaAccion } from './pe-acciones';
import { sectoresPorQs } from './pe-resultados';
import type { Traza } from './pe-tipos';
import { numEc } from './pe-unidades';
import { ZONA_ECUADOR } from '../fechas';
import { continuidadDelPlan, TITULO_CONTINUIDAD, type DatosVista, type Linea } from './pe-vista-previa';
import { trazasContinuidad } from './pe-continuidad';
import { armarVistaPorCapitulos, etiquetaVista, tieneContinuidad, vistaDePaso } from './pe-capitulos';

export const TITULO_PLAN = 'Plan de respuesta ante emergencias';
export const MARCA_BORRADOR = 'BORRADOR';

export type RolFirmaPlan = 'elabora' | 'representante_legal';
export const ETIQUETA_ROL_FIRMA: Record<RolFirmaPlan, string> = {
  elabora: 'Elaborado por (responsable de SST)',
  representante_legal: 'Representante legal',
};

export interface FirmaPDF { rol: RolFirmaPlan; nombre: string; cargo?: string | null; firmadoEn: string; imagen: string | null }

export interface DatosPlanPDF {
  vista: DatosVista;
  version: number;
  estado: 'borrador' | 'aprobada';
  fecha: Date;                       // de la aprobación, o de generación del borrador
  firmas: FirmaPDF[];
  trabajadores?: { id: string; full_name: string }[];
  marcaDeAgua: boolean;
}

// La fuente estándar de jsPDF (WinAnsi) no tiene ≤, ≥, →, ✓, ✕ ni Σ: se cambian por equivalentes legibles.
const REEMPLAZOS: [RegExp, string][] = [[/≤/g, '<='], [/≥/g, '>='], [/→/g, '->'], [/←/g, '<-'], [/✓/g, 'OK'], [/✕/g, 'X'],
  [/Σ/g, 'Suma de '], [/·/g, '·'], [/[‐‑‒]/g, '-'], [/ /g, ' ']];
const WINANSI_EXTRA = '€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ';
export function paraPdf(texto: string): string {
  let t = texto;
  for (const [re, r] of REEMPLAZOS) t = t.replace(re, r);
  return Array.from(t).map(c => (c.charCodeAt(0) <= 0xff || WINANSI_EXTRA.includes(c) ? c : '?')).join('');
}

export const ETIQUETA_ESTADO_TRAZA: Record<Traza['estado'], string> = {
  cumple: 'Cumple', no_cumple: 'No cumple', por_validar: 'Por validar', informativo: 'Informativo',
};

export const valorTraza = (v: number | string | null): string => (v === null ? '—' : typeof v === 'number' ? numEc(v) : v);

// Un instante (aprobación del plan, approved_at o generación del borrador) en el día de Ecuador, sin depender de la
// zona del equipo.
export function fechaEc(d: Date): string {
  return d.toLocaleDateString('es-EC', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: ZONA_ECUADOR });
}

export function nombreArchivoPlan(codigo: string | null | undefined, version: number, borrador: boolean): string {
  const limpio = (codigo || 'plan').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return `Plan_emergencias_${limpio}_v${version}${borrador ? '_borrador' : ''}.pdf`;
}

// PE-C1: clave = de qué evaluación sale (para decir en qué capítulo se completa).
export interface Pendiente { grupo: string; articulo: string; falta: string; paso: number | null; clave?: string }
export function pendientes(v: DatosVista): Pendiente[] {
  const grupos = [...sectoresPorQs(v.sectores).trazas.map(g => ({ ...g, clave: 'carga_fuego' })),
    ...v.evaluaciones.map(e => ({ titulo: e.titulo, trazas: e.trazas, clave: e.clave })),
    { titulo: TITULO_CONTINUIDAD, trazas: trazasContinuidad(continuidadDelPlan(v.plan)), clave: 'continuidad' }];
  return grupos.flatMap(g => g.trazas.filter(t => t.estado === 'por_validar')
    .map(t => ({ grupo: g.titulo, articulo: t.articulo_o_tabla, falta: t.nota ?? 'Falta un dato', paso: t.paso ?? null, clave: g.clave })));
}

/** Dónde se completa un pendiente en la pantalla por capítulos (la continuidad, en su pestaña). */
export function dondeSeCompleta(x: Pick<Pendiente, 'paso' | 'clave'>): string | null {
  if (x.clave === 'continuidad') return 'la pestaña Continuidad del negocio y póliza';
  if (!x.paso) return null;
  const d = vistaDePaso(x.paso, x.clave);
  return etiquetaVista(d.vista, d.sub);
}

// PE-C1: trazas de resultados que acompañan a cada subapartado de los capítulos.
export function trazasPorSubapartado(v: DatosVista): Record<string, { titulo: string; trazas: Traza[] }[]> {
  const ev = (prefijo: string) => v.evaluaciones.filter(e => e.clave.startsWith(prefijo)).map(e => ({ titulo: e.titulo, trazas: e.trazas }));
  return {
    sectores: sectoresPorQs(v.sectores).trazas, meseri: ev('meseri'), amenazas: ev('amenaza_vulnerabilidad'), verificacion: ev('verificacion'),
    aforo: ev('aforo_egreso'), rset: ev('rset'), 'punto-encuentro': ev('punto_encuentro'),
  };
}

export function responsableDe(a: FilaAccion, trabajadores: { id: string; full_name: string }[] = []): string {
  const t = a.responsible_worker_id ? trabajadores.find(x => x.id === a.responsible_worker_id)?.full_name : null;
  return t || a.responsible_role || 'Sin responsable';
}

const MARGEN = 15;
const ANCHO = 210 - MARGEN * 2;
const ALTO_UTIL = 297 - 20;
const ARRIBA = 22; // debajo del encabezado de cada página

export function generarPlanPDF(d: DatosPlanPDF): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const v = d.vista; const p = v.plan;
  const porCapitulos = armarVistaPorCapitulos({ ...v, hoy: d.fecha });
  const trazas = trazasPorSubapartado(v);
  const vigentes = new Set(v.evaluaciones.flatMap(e => e.acciones.map(x => x.sourceKey)));
  let y = ARRIBA;
  const T = (s: string) => paraPdf(s);

  // Marca de agua del borrador: se dibuja al crear cada página, debajo del contenido (no tapa el texto).
  const marca = () => {
    if (!d.marcaDeAgua) return;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(80); doc.setTextColor(241, 245, 249);
    doc.text(MARCA_BORRADOR, 105, 175, { align: 'center', angle: 45 });
    doc.setTextColor(15, 23, 42);
  };
  const nuevaPagina = () => { doc.addPage(); marca(); y = ARRIBA; };
  const espacio = (alto: number) => { if (y + alto > ALTO_UTIL) nuevaPagina(); };
  const texto = (s: string, tam = 9, opc: { negrita?: boolean; sangria?: number; gris?: boolean; color?: [number, number, number] } = {}) => {
    doc.setFont('helvetica', opc.negrita ? 'bold' : 'normal'); doc.setFontSize(tam);
    if (opc.color) doc.setTextColor(...opc.color); else if (opc.gris) doc.setTextColor(100, 116, 139); else doc.setTextColor(15, 23, 42);
    const sangria = opc.sangria ?? 0;
    (doc.splitTextToSize(T(s), ANCHO - sangria) as string[]).forEach(l => { espacio(tam * 0.45); doc.text(l, MARGEN + sangria, y); y += tam * 0.45; });
    doc.setTextColor(15, 23, 42);
  };
  const titulo = (s: string) => {
    espacio(14); y += 3;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11.5); doc.setTextColor(29, 111, 163);
    doc.text(T(s), MARGEN, y);
    doc.setDrawColor(203, 213, 225); doc.line(MARGEN, y + 1.5, MARGEN + ANCHO, y + 1.5);
    y += 6.5; doc.setTextColor(15, 23, 42);
  };
  const colorEstado = (e: Traza['estado']): [number, number, number] =>
    (e === 'no_cumple' ? [185, 28, 28] : e === 'cumple' ? [21, 128, 61] : e === 'por_validar' ? [180, 83, 9] : [71, 85, 105]);
  const linea = (l: Linea) => {
    const prefijo = l.estado && l.estado !== 'informativo' ? `[${ETIQUETA_ESTADO_TRAZA[l.estado]}] ` : '';
    texto(`${prefijo}${l.texto}`, 9, { gris: !!l.pendiente, color: l.estado && l.estado !== 'informativo' ? colorEstado(l.estado) : undefined, sangria: 2 });
    y += 0.6;
  };
  const traza = (t: Traza) => {
    espacio(12);
    texto(`${ETIQUETA_ESTADO_TRAZA[t.estado]} · ${t.norma} · ${t.articulo_o_tabla}`, 8.5, { negrita: true, color: colorEstado(t.estado), sangria: 4 });
    const umb = t.umbral === null ? '' : `Umbral: ${valorTraza(t.umbral)} · `;
    texto(`${umb}Valor: ${valorTraza(t.valor)}${t.unidad && t.unidad !== '—' ? ` ${t.unidad}` : ''}`, 8, { sangria: 4 });
    if (t.nota) texto(t.nota, 7.5, { gris: true, sangria: 4 });
    y += 1;
  };

  // ── Portada ──
  marca();
  doc.setFillColor(15, 23, 42); doc.rect(0, 0, 210, 70, 'F');
  doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(20);
  doc.text(T(TITULO_PLAN), MARGEN, 35);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(12);
  doc.text(T(v.empresa || 'Empresa'), MARGEN, 48);
  doc.setTextColor(15, 23, 42);
  y = 85;
  const sede = p?.site_address?.trim() || [p?.site_canton, p?.site_province].filter(Boolean).join(', ') || 'Pendiente';
  const elabora = d.firmas.find(f => f.rol === 'elabora');
  const repLegal = d.firmas.find(f => f.rol === 'representante_legal');
  for (const [k, val] of [
    ['Empresa', v.empresa || 'Pendiente'], ['Sede o dirección', sede], ['Código del plan', p?.code || 'Pendiente (lo define la empresa)'],
    ['Versión', String(d.version)], ['Fecha', fechaEc(d.fecha)], ['Estado', d.estado === 'aprobada' ? 'Aprobada' : 'Borrador (sin aprobar)'],
    ['Responsable de la elaboración', elabora?.nombre || 'Pendiente (se firma al aprobar)'], ['Representante legal', repLegal?.nombre || 'Pendiente (se firma al aprobar)'],
  ] as [string, string][]) {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.text(T(`${k}:`), MARGEN, y);
    doc.setFont('helvetica', 'normal');
    const partes = doc.splitTextToSize(T(val), ANCHO - 60) as string[];
    for (let i = 0; i < partes.length; i++) doc.text(partes[i], MARGEN + 60, y + i * 5);
    y += 8;
  }
  nuevaPagina();

  // ── Capítulos 1 a 8 (y la continuidad del negocio al final, si tiene datos) ──
  const subtitulo = (t: string) => { espacio(10); y += 1; texto(t, 10, { negrita: true }); y += 0.8; };
  for (const c of porCapitulos.capitulos) {
    titulo(`${c.numero}. ${c.titulo}`);
    for (const s of c.subsecciones) {
      subtitulo(`${s.numero} ${s.titulo}`);
      s.lineas.forEach(linea);
      if (s.secciones.includes('5')) {
        const acciones = v.acciones.filter(a => !origenResuelto(a, vigentes));
        const resueltas = v.acciones.filter(a => origenResuelto(a, vigentes));
        texto('Plan de acción', 9.5, { negrita: true }); y += 0.5;
        if (acciones.length === 0) texto('Sin acciones vigentes.', 8.5, { gris: true, sangria: 2 });
        for (let i = 0; i < acciones.length; i++) {
          const a = acciones[i];
          texto(`${i + 1}. ${a.description}${a.is_mandatory ? ' (obligatoria)' : ''}`, 8.5, { sangria: 2 });
          texto(`Responsable: ${responsableDe(a, d.trabajadores)} · Fecha compromiso: ${a.due_date ?? 'sin fecha'} · Estado: ${ETIQUETA_ESTADO_ACCION[a.status]}`, 8, { gris: true, sangria: 6 });
          y += 0.8;
        }
        if (resueltas.length) {
          texto('Resueltas por cambio de origen (ya no se programan)', 9, { negrita: true }); y += 0.5;
          resueltas.forEach(a => texto(`· ${a.description} (${ETIQUETA_ESTADO_ACCION[a.status]})`, 8, { gris: true, sangria: 2 }));
        }
      }
      for (const g of trazas[s.id] ?? []) {
        if (!g.trazas.length) continue;
        texto(`Resultados: ${g.titulo}`, 9, { negrita: true }); y += 0.5;
        g.trazas.forEach(traza);
      }
    }
  }

  // ── Firmas (PE-26) ──
  titulo('Firmas');
  espacio(46);
  const anchoCol = (ANCHO - 6) / 2; const y0 = y;
  (['elabora', 'representante_legal'] as RolFirmaPlan[]).forEach((rol, i) => {
    const f = d.firmas.find(x => x.rol === rol);
    const x = MARGEN + i * (anchoCol + 6);
    doc.setDrawColor(203, 213, 225); doc.rect(x, y0, anchoCol, 26);
    if (f?.imagen) { try { doc.addImage(f.imagen, 'PNG', x + 2, y0 + 1, anchoCol - 4, 24); } catch { /* imagen dañada: queda el recuadro */ } }
    doc.setFontSize(8.5); doc.setFont('helvetica', 'bold'); doc.text(T(ETIQUETA_ROL_FIRMA[rol]), x, y0 + 30);
    doc.setFont('helvetica', 'normal');
    doc.text(T(f ? `${f.nombre}${f.cargo ? ` · ${f.cargo}` : ''}` : 'Firma pendiente: se toma al aprobar la versión'), x, y0 + 34);
    if (f) doc.text(T(new Date(f.firmadoEn).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' })), x, y0 + 38);
  });
  y = y0 + 44;
  espacio(40);
  doc.setDrawColor(148, 163, 184); doc.rect(MARGEN, y, ANCHO, 32);
  doc.setFontSize(8.5); doc.setTextColor(100, 116, 139);
  doc.text(T('Espacio reservado para el sello de aprobación del Cuerpo de Bomberos'), MARGEN + 3, y + 6);
  doc.setTextColor(15, 23, 42); y += 38;

  // ── Anexo de pendientes ── (la continuidad es opcional: sin datos no aparece en el plan ni en sus pendientes)
  titulo('Anexo: pendientes ("por validar")');
  const pend = pendientes(v).filter(x => x.clave !== 'continuidad' || tieneContinuidad(p));
  if (pend.length === 0) texto('No hay pendientes.', 9, { gris: true });
  pend.forEach((x, i) => {
    texto(`${i + 1}. ${x.grupo} · ${x.articulo}`, 8.5, { negrita: true, sangria: 2 });
    const donde = dondeSeCompleta(x);
    texto(`Falta: ${x.falta}${donde ? ` · Se completa en ${donde}` : ''}`, 8, { gris: true, sangria: 6 });
    y += 0.6;
  });

  // ── Encabezado y pie en todas las páginas (la marca de agua ya va debajo, en cada una) ──
  const paginas = doc.getNumberOfPages();
  const pie = `Código ${p?.code || 'sin código'} · Versión ${d.version} · ${fechaEc(d.fecha)}`;
  for (let n = 1; n <= paginas; n++) {
    doc.setPage(n);
    if (n > 1) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(100, 116, 139);
      doc.text(T(v.empresa || ''), MARGEN, 10);
      doc.text(T(TITULO_PLAN), 210 - MARGEN, 10, { align: 'right' });
      doc.setDrawColor(226, 232, 240); doc.line(MARGEN, 12, 210 - MARGEN, 12);
    }
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(100, 116, 139);
    doc.text(T(pie), MARGEN, 290);
    doc.text(T(`Página ${n} de ${paginas}`), 210 - MARGEN, 290, { align: 'right' });
    doc.setTextColor(15, 23, 42);
  }
  return doc;
}
